// Overlap detection combining geometry (for circles) and administrative containment
// Returns whether any overlap exists and which marker pairs overlap

// Extract country/region/city levels from Google address_components
const extractAdminLevels = (addressComponents = []) => {
  const find = (type) =>
    addressComponents.find((c) => (c.types || []).includes(type));
  const country = find("country");
  const admin1 = find("administrative_area_level_1");
  const admin2 = find("administrative_area_level_2");
  const admin3 = find("administrative_area_level_3");
  const locality = find("locality");
  const sublocality = find("sublocality");

  return {
    country: country?.long_name || country?.short_name || null,
    admin1: admin1?.long_name || admin1?.short_name || null,
    admin2: admin2?.long_name || admin2?.short_name || null,
    admin3: admin3?.long_name || admin3?.short_name || null,
    locality: locality?.long_name || locality?.short_name || null,
    sublocality: sublocality?.long_name || sublocality?.short_name || null,
  };
};

// Quick check that at least one administrative level exists
const hasAnyLevel = (levels) => {
  if (!levels) return false;
  return Boolean(
    levels.country ||
      levels.admin1 ||
      levels.admin2 ||
      levels.admin3 ||
      levels.locality ||
      levels.sublocality
  );
};

// Geometry helpers
const EARTH_RADIUS_M = 6371000;

const toRadians = (deg) => (deg * Math.PI) / 180;

// Haversine distance between two lat/lng points in meters
const haversineDistanceMeters = (a, b) => {
  const dLat = toRadians((b.lat || 0) - (a.lat || 0));
  const dLng = toRadians((b.lng || 0) - (a.lng || 0));
  const lat1 = toRadians(a.lat || 0);
  const lat2 = toRadians(b.lat || 0);
  const sinDlat = Math.sin(dLat / 2);
  const sinDlng = Math.sin(dLng / 2);
  const h =
    sinDlat * sinDlat + Math.cos(lat1) * Math.cos(lat2) * sinDlng * sinDlng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_M * c;
};

const getViewport = (marker) => marker?.metadata?.geometry?.viewport || null;

const getLocation = (marker) =>
  marker?.metadata?.geometry?.location || { lat: marker.lat, lng: marker.lng };

const pointInRect = (point, rect) => {
  if (!point || !rect) return false;
  const { south, west, north, east } = rect;
  if ([south, west, north, east].some((v) => typeof v !== "number"))
    return false;
  return (
    point.lat >= south &&
    point.lat <= north &&
    point.lng >= west &&
    point.lng <= east
  );
};

const pointInCircle = (point, center, radiusM) => {
  if (!point || !center || !radiusM) return false;
  return haversineDistanceMeters(point, center) <= radiusM;
};

// Circle vs rectangle (viewport) intersection test
const circleIntersectsRect = (center, radiusM, rect) => {
  if (!center || !radiusM || !rect) return false;
  // Center inside rect
  if (pointInRect(center, rect)) return true;
  // Closest point on rect to circle center
  const clampedLat = Math.min(Math.max(center.lat, rect.south), rect.north);
  const clampedLng = Math.min(Math.max(center.lng, rect.west), rect.east);
  const closest = { lat: clampedLat, lng: clampedLng };
  return haversineDistanceMeters(center, closest) <= radiusM;
};

// Size heuristic to order conflicts (A = superior/larger, B = inferior/smaller)
// - Circles by radius^2 (proportional to area)
// - Areas by viewport area (latSpan * lngSpan)
// - Points default to 0
const getMarkerSizeScore = (marker) => {
  if (!marker) return 0;
  if (marker.type === "radius" && Number(marker.radius) > 0) {
    const r = Number(marker.radius) || 0;
    return r * r;
  }
  const viewport = getViewport(marker);
  if (viewport) {
    const { south, west, north, east } = viewport;
    if (
      [south, west, north, east].every((v) => typeof v === "number") &&
      north >= south &&
      east >= west
    ) {
      const latSpan = north - south;
      const lngSpan = east - west;
      return latSpan * lngSpan;
    }
  }
  return 0;
};

// Normalize the conflict so that `a` is always the superior (larger) location
// Priority rule: non-radius (specific/area) wins over radius when in conflict
const normalizeConflict = (a, b, aIndex, bIndex, reason) => {
  const aIsRadius = a?.type === "radius";
  const bIsRadius = b?.type === "radius";

  if (aIsRadius && !bIsRadius) {
    return { a: b, b: a, aIndex: bIndex, bIndex: aIndex, reason };
  }
  if (bIsRadius && !aIsRadius) {
    return { a, b, aIndex, bIndex, reason };
  }

  const sizeA = getMarkerSizeScore(a);
  const sizeB = getMarkerSizeScore(b);
  if (sizeB > sizeA) {
    return { a: b, b: a, aIndex: bIndex, bIndex: aIndex, reason };
  }
  return { a, b, aIndex, bIndex, reason };
};

// Administrative containment: does parentLevels include childLevels?
// Only considered when parent is a high-level area (country/admin1/admin2)
const adminContains = (childLevels, parentLevels) => {
  if (!childLevels || !parentLevels) return false;
  if (!hasAnyLevel(parentLevels) || !hasAnyLevel(childLevels)) return false;

  const isHighLevelParent = Boolean(
    parentLevels.country || parentLevels.admin1 || parentLevels.admin2
  );
  if (!isHighLevelParent) return false;

  if (parentLevels.country && parentLevels.country !== childLevels.country)
    return false;
  if (parentLevels.admin1 && parentLevels.admin1 !== childLevels.admin1)
    return false;
  if (parentLevels.admin2 && parentLevels.admin2 !== childLevels.admin2)
    return false;
  if (parentLevels.admin3 && parentLevels.admin3 !== childLevels.admin3)
    return false;
  if (parentLevels.locality && parentLevels.locality !== childLevels.locality)
    return false;
  if (
    parentLevels.sublocality &&
    parentLevels.sublocality !== childLevels.sublocality
  )
    return false;

  return true;
};

const classify = (marker) => {
  if (marker?.type === "radius" && Number(marker.radius) > 0) return "circle";
  return "area_or_point";
};

const getLevels = (marker) =>
  extractAdminLevels(marker?.metadata?.address_components || []);

const samePlace = (a, b) => {
  const aId = a?.metadata?.place_id || a?.id;
  const bId = b?.metadata?.place_id || b?.id;
  return Boolean(aId && bId && aId === bId);
};

export const detectOverlappingLocations = (markers = []) => {
  const conflicts = [];

  for (let i = 0; i < markers.length; i++) {
    for (let j = i + 1; j < markers.length; j++) {
      const a = markers[i];
      const b = markers[j];

      // Same place_id -> overlap
      if (samePlace(a, b)) {
        conflicts.push(normalizeConflict(a, b, i, j, "same_place_id"));
        continue;
      }

      const typeA = classify(a);
      const typeB = classify(b);
      const levelsA = getLevels(a);
      const levelsB = getLevels(b);
      const hasLevelsA = hasAnyLevel(levelsA);
      const hasLevelsB = hasAnyLevel(levelsB);

      // Geometry-first validations for circles
      if (typeA === "circle" && typeB === "circle") {
        const centerA = getLocation(a);
        const centerB = getLocation(b);
        const rA = Number(a.radius) || 0;
        const rB = Number(b.radius) || 0;
        const distance = haversineDistanceMeters(centerA, centerB);

        // Only warn if one circle is completely inside the other
        // (not just overlapping)
        if (distance + Math.min(rA, rB) <= Math.max(rA, rB)) {
          conflicts.push(normalizeConflict(a, b, i, j, "circle_circle"));
          continue;
        }
      }

      if (typeA === "circle" && typeB !== "circle") {
        const centerA = getLocation(a);
        const rA = Number(a.radius) || 0;
        const viewportB = getViewport(b);
        if (viewportB && circleIntersectsRect(centerA, rA, viewportB)) {
          conflicts.push(normalizeConflict(a, b, i, j, "circle_area"));
          continue;
        }
        const centerB = getLocation(b);
        if (pointInCircle(centerB, centerA, rA)) {
          conflicts.push(normalizeConflict(a, b, i, j, "circle_point"));
          continue;
        }
      }

      if (typeB === "circle" && typeA !== "circle") {
        const centerB = getLocation(b);
        const rB = Number(b.radius) || 0;
        const viewportA = getViewport(a);
        if (viewportA && circleIntersectsRect(centerB, rB, viewportA)) {
          conflicts.push(normalizeConflict(a, b, i, j, "circle_area"));
          continue;
        }
        const centerA = getLocation(a);
        if (pointInCircle(centerA, centerB, rB)) {
          conflicts.push(normalizeConflict(a, b, i, j, "circle_point"));
          continue;
        }
      }

      // If either marker lacks administrative levels, skip admin-based checks below
      if (!hasLevelsA || !hasLevelsB) continue;

      // Administrative containment between areas/points
      const aInB = adminContains(levelsA, levelsB);
      const bInA = adminContains(levelsB, levelsA);

      if (aInB || bInA) {
        const reason =
          aInB && bInA ? "mutual_admin_match" : aInB ? "a_in_b" : "b_in_a";
        conflicts.push(normalizeConflict(a, b, i, j, reason));
        continue;
      }
    }
  }

  return { hasOverlap: conflicts.length > 0, conflicts };
};

export default detectOverlappingLocations;
